import React, { useState, useEffect } from 'react';
import { healthAPI } from '../api';
import { KnowledgeItem } from '../types';
import { Search, Edit2, Plus, ArrowLeft, Save, Loader, Trash2 } from 'lucide-react';
import './KnowledgePage.css';

interface KnowledgePageProps {
    onNavigate: (view: any) => void;
}

export const KnowledgePage: React.FC<KnowledgePageProps> = ({ onNavigate }) => {
    const [items, setItems] = useState<KnowledgeItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState<KnowledgeItem | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState('');

    // Research State
    const [showResearchModal, setShowResearchModal] = useState(false);
    const [researchTopic, setResearchTopic] = useState('');
    const [isResearching, setIsResearching] = useState(false);

    useEffect(() => {
        loadKnowledge();
    }, []);

    const loadKnowledge = async () => {
        setIsLoading(true);
        try {
            const response = await healthAPI.getKnowledge();
            setItems(response.items);
        } catch (error) {
            console.error('Error loading knowledge:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectItem = async (slug: string) => {
        setIsLoading(true);
        try {
            const item = await healthAPI.getKnowledgeItem(slug);
            setSelectedItem({ ...item, slug });
            setEditContent(item.content || '');
            setIsEditing(false);
        } catch (error) {
            console.error('Error loading item:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedItem) return;
        setIsLoading(true);
        try {
            await healthAPI.updateKnowledgeItem(selectedItem.slug, editContent);
            setSelectedItem({ ...selectedItem, content: editContent, source: 'User Edit' });
            setIsEditing(false);
            loadKnowledge(); // Refresh list
        } catch (error) {
            console.error('Error saving item:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedItem) return;
        if (!window.confirm(`Delete "${selectedItem.topic}"? This cannot be undone.`)) return;
        setIsLoading(true);
        try {
            await healthAPI.deleteKnowledgeItem(selectedItem.slug);
            setSelectedItem(null);
            loadKnowledge();
        } catch (error) {
            console.error('Error deleting item:', error);
            alert('Failed to delete item.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!researchTopic.trim()) return;

        setIsResearching(true);
        try {
            await healthAPI.researchTopic(researchTopic);
            setShowResearchModal(false);
            setResearchTopic('');
            loadKnowledge(); // Refresh list to see new item
        } catch (error) {
            console.error('Error researching:', error);
            alert('Research failed. Please check backend logs.');
        } finally {
            setIsResearching(false);
        }
    };

    if (selectedItem) {
        return (
            <div className="knowledge-detail">
                <div className="detail-header">
                    <button className="back-button" onClick={() => setSelectedItem(null)}>
                        <ArrowLeft size={16} /> Back
                    </button>
                    <h2>{selectedItem.topic}</h2>
                    <div className="detail-actions">
                        {!isEditing ? (
                            <>
                                <button className="delete-button" onClick={handleDelete} disabled={isLoading}>
                                    <Trash2 size={16} /> Delete
                                </button>
                                <button className="edit-button" onClick={() => setIsEditing(true)}>
                                    <Edit2 size={16} /> Edit
                                </button>
                            </>
                        ) : (
                            <button className="save-button" onClick={handleSave} disabled={isLoading}>
                                <Save size={16} /> Save
                            </button>
                        )}
                    </div>
                </div>

                <div className="item-meta">
                    <span>Source: {selectedItem.source}</span>
                    <span>Updated: {new Date(selectedItem.updated_at).toLocaleDateString()}</span>
                </div>

                {isEditing ? (
                    <textarea
                        className="content-editor"
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                    />
                ) : (
                    <div className="content-preview markdown-body">
                        <pre>{selectedItem.content}</pre>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="knowledge-page">
            <div className="page-header">
                <h2>Knowledge Base</h2>
                <button className="add-button" onClick={() => setShowResearchModal(true)}>
                    <Plus size={16} /> New Research
                </button>
            </div>

            {isLoading && <div className="loading">Loading...</div>}

            <div className="knowledge-list">
                {items.map((item) => (
                    <div key={item.slug} className="knowledge-card" onClick={() => handleSelectItem(item.slug)}>
                        <h3>{item.topic}</h3>
                        <div className="card-meta">
                            <span>{item.source}</span>
                            <span>{new Date(item.updated_at).toLocaleDateString()}</span>
                        </div>
                    </div>
                ))}
            </div>

            {showResearchModal && (
                <div className="modal-overlay">
                    <div className="research-modal">
                        <h3>Start New Research</h3>
                        <form onSubmit={handleResearch}>
                            <p>Enter a topic to research. The agent will search reliable medical sources and synthesize a summary.</p>
                            <input
                                type="text"
                                placeholder="e.g., Benefits of Ginger"
                                value={researchTopic}
                                onChange={(e) => setResearchTopic(e.target.value)}
                                disabled={isResearching}
                                autoFocus
                            />
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowResearchModal(false)} disabled={isResearching}>
                                    Cancel
                                </button>
                                <button type="submit" disabled={isResearching}>
                                    {isResearching ? <><Loader className="spin" size={14} /> Researching...</> : 'Start Research'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
